// Email notification service using EmailJS
import emailjs from '@emailjs/browser';
import { EMAIL_CONFIG } from '../config/email';
import { debugEmailConfig } from './emailDebug';

interface AccessNotificationData {
  email: string;
  name?: string;
  accessType: 'full' | 'demo';
  timestamp: string;
  userAgent?: string;
}

/**
 * Send email notification when someone accesses the app
 */
export async function sendAccessNotification(data: AccessNotificationData): Promise<boolean> {
  // Debug configuration on first call
  if (typeof window !== 'undefined' && !(window as any).__emailDebugShown) {
    debugEmailConfig();
    (window as any).__emailDebugShown = true;
  }

  // Skip if email notifications are disabled or not configured
  if (!EMAIL_CONFIG.ENABLED) {
    console.log('📧 Email notifications are disabled');
    return false;
  }

  if (!EMAIL_CONFIG.PUBLIC_KEY || !EMAIL_CONFIG.SERVICE_ID || !EMAIL_CONFIG.TEMPLATE_ID) {
    console.warn('📧 Email notifications not configured. Missing:', {
      PUBLIC_KEY: !EMAIL_CONFIG.PUBLIC_KEY,
      SERVICE_ID: !EMAIL_CONFIG.SERVICE_ID,
      TEMPLATE_ID: !EMAIL_CONFIG.TEMPLATE_ID,
      NOTIFICATION_EMAIL: !EMAIL_CONFIG.NOTIFICATION_EMAIL,
    });
    console.warn('Please configure EmailJS in your .env file. See EMAIL_SETUP.md for instructions.');
    return false;
  }

  try {
    // Format the timestamp
    const date = new Date(data.timestamp);
    const formattedDate = date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });

    // Prepare email template parameters
    const templateParams = {
      to_email: EMAIL_CONFIG.NOTIFICATION_EMAIL,
      user_email: data.email,
      user_name: data.name || 'Not provided',
      access_type: data.accessType === 'full' ? 'Full Access' : 'Demo Mode',
      timestamp: formattedDate,
      user_agent: data.userAgent || 'Unknown',
      subject: `New ${data.accessType === 'full' ? 'Sign-In' : 'Demo Access'} - Dynami MVP`,
    };

    console.log('📧 Attempting to send email notification...', {
      serviceId: EMAIL_CONFIG.SERVICE_ID,
      templateId: EMAIL_CONFIG.TEMPLATE_ID,
      toEmail: EMAIL_CONFIG.NOTIFICATION_EMAIL,
    });

    // Send email using EmailJS v3+ API
    const response = await emailjs.send(
      EMAIL_CONFIG.SERVICE_ID,
      EMAIL_CONFIG.TEMPLATE_ID,
      templateParams,
      {
        publicKey: EMAIL_CONFIG.PUBLIC_KEY,
      }
    );

    console.log('✅ Email notification sent successfully:', response);
    return true;
  } catch (error: any) {
    console.error('❌ Error sending email notification:', error);
    
    // Provide helpful error messages
    if (error?.text) {
      console.error('Error details:', error.text);
    }
    
    if (error?.status) {
      console.error('HTTP Status:', error.status);
      
      // Common error codes
      if (error.status === 400) {
        console.error('Bad Request - Check your template parameters match your EmailJS template');
      } else if (error.status === 401) {
        console.error('Unauthorized - Check your Public Key is correct');
      } else if (error.status === 404) {
        console.error('Not Found - Check your Service ID and Template ID are correct');
      }
    }
    
    // Don't throw - we don't want to block user access if email fails
    return false;
  }
}

/**
 * Send a test email to verify configuration
 */
export async function sendTestEmail(): Promise<boolean> {
  return sendAccessNotification({
    email: 'test@example.com',
    name: 'Test User',
    accessType: 'demo',
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
  });
}

